import Auth from '@aws-amplify/auth'
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js'
import { callWithRetry, RetryOptions } from './retry'

export type CognitoSessionTokens = {
  idToken: string
  accessToken: string
  refreshToken: string
}

export type SignUpAuthPayload = {
  cognitoUsername?: string
  cognitoSession?: CognitoSessionTokens
}

export type SignInAuthResult = {
  user: CognitoUser
  authPayload: SignUpAuthPayload
}

export const AWS_AUTH_RETRY_CONFIG: RetryOptions = {
  timeout: 5 * 1000,
  maxRetries: 3,
  waitBetweenTries: 2 * 1000,
}

const getUserPool = () =>
  new CognitoUserPool({
    UserPoolId: String(process.env.GATSBY_COGNITO_USER_POOL_ID),
    ClientId: String(process.env.GATSBY_COGNITO_CLIENT_ID),
  })

const decodeJwtPayload = (token: string): Record<string, string> => {
  const payload = token.split('.')[1]
  if (!payload) return {}
  return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
}

export const establishCognitoSession = (
  cognitoUsername: string,
  tokens: CognitoSessionTokens
): CognitoUser => {
  const userPool = getUserPool()
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.idToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.accessToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refreshToken }),
  })

  const user = new CognitoUser({
    Username: cognitoUsername,
    Pool: userPool,
  })
  user.setSignInUserSession(session)

  const tokenPayload = decodeJwtPayload(tokens.idToken)
  Object.assign(user, {
    attributes: {
      'custom:user_id': tokenPayload['custom:user_id'],
      email: tokenPayload.email,
    },
  })

  return user
}

export const getAuthAccessToken = async (
  user: CognitoUser,
  signUpPayload?: SignUpAuthPayload
): Promise<string> => {
  const sessionFromUser = user.getSignInUserSession()
  if (sessionFromUser) {
    return sessionFromUser.getAccessToken().getJwtToken()
  }

  if (signUpPayload?.cognitoSession?.accessToken) {
    return signUpPayload.cognitoSession.accessToken
  }

  const amplifySession = await Auth.currentSession()
  return amplifySession.getAccessToken().getJwtToken()
}

export const getUserIdFromAuth = (
  user: CognitoUser,
  signUpPayload?: SignUpAuthPayload
): string => {
  const fromAttributes = user.attributes?.['custom:user_id']
  if (fromAttributes) {
    return String(fromAttributes)
  }

  const idToken = signUpPayload?.cognitoSession?.idToken
  if (idToken) {
    const userId = decodeJwtPayload(idToken)['custom:user_id']
    if (userId) return userId
  }

  throw new Error('Unable to resolve user id from auth session')
}

export const completeSignUpAuth = async (
  signUpPayload: SignUpAuthPayload | undefined,
  email: string,
  password: string,
  retryConfig: RetryOptions
): Promise<CognitoUser> => {
  await Auth.signOut().catch(() => undefined)

  if (signUpPayload?.cognitoSession && signUpPayload?.cognitoUsername) {
    const user = establishCognitoSession(
      signUpPayload.cognitoUsername,
      signUpPayload.cognitoSession
    )

    await callWithRetry(() => Auth.currentSession(), retryConfig).catch(() => null)

    return user
  }

  const username = signUpPayload?.cognitoUsername || email
  return callWithRetry(() => Auth.signIn(username, password), retryConfig)
}

export const resolveSignInSession = async (
  user: CognitoUser,
  authPayload?: SignUpAuthPayload,
  retryConfig: RetryOptions = AWS_AUTH_RETRY_CONFIG
) => {
  const accessToken = await getAuthAccessToken(user, authPayload)
  const session = await callWithRetry(
    () => Auth.currentSession(),
    retryConfig
  ).catch(() => null)

  return {
    accessToken,
    session,
    userId: getUserIdFromAuth(user, authPayload),
  }
}

export const establishSignInAuth = async (
  authPayload: SignUpAuthPayload | undefined,
  email: string,
  password: string,
  retryConfig: RetryOptions = AWS_AUTH_RETRY_CONFIG
): Promise<SignInAuthResult> => {
  await Auth.signOut().catch(() => undefined)

  const normalizedEmail = email.trim().toLowerCase()
  const payload = authPayload || {}

  if (payload.cognitoSession && payload.cognitoUsername) {
    const user = establishCognitoSession(
      payload.cognitoUsername,
      payload.cognitoSession
    )
    await callWithRetry(() => Auth.currentSession(), retryConfig).catch(() => null)
    return { user, authPayload: payload }
  }

  const username = payload.cognitoUsername || normalizedEmail
  const user = await callWithRetry(
    () => Auth.signIn(username, password),
    retryConfig
  )
  return { user, authPayload: payload }
}
